import { Tabs, Tab, TabList, Flex } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import { PortfolioStatementsExpensesFeesContainer } from './PortfolioStatementsExpensesFeesContainer';
import { PortfolioStatementsOutstandingLedgerContainer } from './PortfolioStatementsOutstandingLedgerContainer';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { MonthlySummaryViewContainer } from '~/packages/statements/ui/MonthlySummaryViewContainer';

// Types
enum StatementsTabOptionKeys {
  outstandingLedger = 'outstandingLedger',
  expensesFees = 'expensesFees',
  monthlySummary = 'monthlySummary',
}
type StatementsTabConfig = {
  key: StatementsTabOptionKeys;
  label: string;
};

// Constants
const statementsTabOptions: Record<
  StatementsTabOptionKeys,
  StatementsTabConfig
> = {
  outstandingLedger: {
    key: StatementsTabOptionKeys.outstandingLedger,
    label: 'Outstanding Ledger',
  },
  expensesFees: {
    key: StatementsTabOptionKeys.expensesFees,
    label: 'Facility Expenses & Fees',
  },
  monthlySummary: {
    key: StatementsTabOptionKeys.monthlySummary,
    label: 'Monthly Summary',
  },
};

// Exports
type PortfolioStatementsContainerProps = {
  creditFacilityId: string;
};
export const PortfolioStatementsContainer = ({
  creditFacilityId,
}: PortfolioStatementsContainerProps) => {
  const flags = useFeatureFlags();
  const [activeTab, setActiveTab] = useState<StatementsTabOptionKeys>(
    StatementsTabOptionKeys.outstandingLedger,
  );

  const tabs = useMemo<StatementsTabConfig[]>(() => {
    const tabsToShow = [
      statementsTabOptions.outstandingLedger,
      statementsTabOptions.expensesFees,
    ];
    if (flags.isStatementsMonthlySummaryEnabled) {
      tabsToShow.push(statementsTabOptions.monthlySummary);
    }
    return tabsToShow;
  }, [flags]);

  return (
    <Flex direction="column" gap="1.5rem">
      <Typography variant="section-title" trim>
        Statements
      </Typography>
      <Tabs
        variant="line"
        index={tabs.findIndex(({ key }) => key === activeTab)}
        colorScheme="blue"
      >
        <TabList>
          {tabs.map(({ label, key }) => (
            <Tab key={key} onClick={() => setActiveTab(key)} mt="0">
              {label}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <Flex direction="column" gap="1rem">
        {activeTab === StatementsTabOptionKeys.outstandingLedger && (
          <PortfolioStatementsOutstandingLedgerContainer
            creditFacilityId={creditFacilityId}
          />
        )}
        {activeTab === StatementsTabOptionKeys.expensesFees && (
          <PortfolioStatementsExpensesFeesContainer
            creditFacilityId={creditFacilityId}
          />
        )}
        {activeTab === StatementsTabOptionKeys.monthlySummary && (
          <MonthlySummaryViewContainer creditFacilityId={creditFacilityId} />
        )}
      </Flex>
    </Flex>
  );
};
